import {OverlayDirectionProvider} from '@github-ui/copilot-chat/contexts/OverlayDirectionContext'
import {
  copilotDockedPanelClosedEvent,
  copilotDockedPanelHeaderID,
  copilotDockedPanelID,
  copilotDockedPanelReadyEvent,
} from '@github-ui/copilot-chat/utils/constants'
import {COPILOT_PATH, isRepository} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {DialogType} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {useChatStateValues} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {getSelectedThread} from '@github-ui/copilot-chat/utils/get-selected-thread'
import {
  useContentPreview,
  useContentPreviewOpen,
} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useNavigate} from '@github-ui/use-navigate'
import {Dialog} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import {CopilotChatPanelContent} from './CopilotChatPanelContent'
import styles from './DockedCopilotChatPortal.module.css'
import {EmbeddedContentPreview} from './EmbeddedContentPreview'
import {Header} from './Header'

export function DockedCopilotChatPortal() {
  const [target, setTarget] = useState<HTMLElement | null>(null)
  const [headerTarget, setHeaderTarget] = useState<HTMLElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const staffDialogRef = useRef<HTMLDivElement>(null)
  const [showStaffDialog, setShowStaffDialog] = useState<DialogType>(DialogType.None)
  const manager = useChatManager()
  const navigate = useNavigate()
  const state = useChatStateValues('currentReferences', 'currentTopic', 'selectedThreadID', 'threads', 'chatIsOpen')
  const thread = getSelectedThread(state)
  const {currentTopic} = state

  // Show a loading state while restoring a PR-associated thread from localStorage
  const [isInitialLoading, setIsInitialLoading] = useState(() => {
    if (!copilotFeatureFlags.prChatThreadPersistence || !ssrSafeWindow) return false
    return !!copilotLocalStorage.getPullRequestThreadID(ssrSafeWindow.location.href)
  })

  // Clear initial loading once the thread is resolved or we know there's nothing to restore
  useEffect(() => {
    if (isInitialLoading && (thread || !state.selectedThreadID)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsInitialLoading(false)
    }
  }, [isInitialLoading, thread, state.selectedThreadID])

  const isContentPreviewOpen = useContentPreviewOpen()
  const {closePreviewPane} = useContentPreview()
  const handleCloseContentPreview = useCallback(() => {
    closePreviewPane()
  }, [closePreviewPane])

  const navigateToImmersive = useCallback(() => {
    sendEvent('dotcom_chat.activate', {target: 'IMMERSIVE_OPTION', mode: 'assistive'})
    copilotLocalStorage.setCurrentReferences(thread?.id ?? null, state.currentReferences)
    copilotLocalStorage.setSavedMessageFast(thread?.id ?? null, 'immersive', inputRef.current?.value ?? '')

    if (thread) {
      navigate(`${COPILOT_PATH}/c/${thread.id}`)
    } else if (currentTopic && isRepository(currentTopic)) {
      navigate(`${COPILOT_PATH}/r/${currentTopic.ownerLogin}/${currentTopic.name}`)
    } else {
      navigate(COPILOT_PATH)
    }
  }, [thread, state.currentReferences, currentTopic, navigate])

  useEffect(() => {
    const findTargets = () => {
      const el = document.getElementById(copilotDockedPanelID)
      const headerEl = document.getElementById(copilotDockedPanelHeaderID)
      setTarget(prev => (prev === el ? prev : (el ?? null)))
      setHeaderTarget(prev => (prev === headerEl ? prev : (headerEl ?? null)))
    }

    const onReady = () => requestAnimationFrame(findTargets)
    const onClose = () => {
      setTarget(null)
      setHeaderTarget(null)
      manager.closeChat()
    }

    document.addEventListener(copilotDockedPanelReadyEvent, onReady)
    document.addEventListener(copilotDockedPanelClosedEvent, onClose)

    // Check if already mounted
    findTargets()

    return () => {
      document.removeEventListener(copilotDockedPanelReadyEvent, onReady)
      document.removeEventListener(copilotDockedPanelClosedEvent, onClose)
    }
  }, [manager])

  // When the docked panel target appears and chat isn't open yet, open it.
  // Normally the button fires publishOpenCopilotChat which calls
  // handleOpenPanelEvent (setting chatIsOpen before we get here), but this
  // handles edge cases where the panel DOM is already mounted.
  useEffect(() => {
    if (target && !state.chatIsOpen) {
      void manager.openChat(null, 'thread', 'docked-panel', undefined, undefined, true)
    }
  }, [target, state.chatIsOpen, manager])

  if (!target) return null

  return (
    <>
      {headerTarget &&
        createPortal(
          <ScopedCommands commands={{'copilot-chat:continue-in-immersive': navigateToImmersive}}>
            <Header
              staffDialogRef={staffDialogRef}
              showStaffDialog={showStaffDialog}
              setShowStaffDialog={setShowStaffDialog}
              hideCloseButton
              forceVisible
            />
          </ScopedCommands>,
          headerTarget,
        )}
      {createPortal(
        <OverlayDirectionProvider preferredOverlaySide="outside-top">
          <div className={styles.dockedPanelContent}>
            <CopilotChatPanelContent inputRef={inputRef} isInitialLoading={isInitialLoading} forceIconOnly />
          </div>
        </OverlayDirectionProvider>,
        target,
      )}
      {isContentPreviewOpen && (
        <Dialog
          className={styles.contentPreviewDialog}
          onClose={handleCloseContentPreview}
          position={{narrow: 'fullscreen', regular: 'fullscreen', wide: 'fullscreen'}}
          /* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */
          title="Content Preview"
          renderHeader={({dialogLabelId}) => (
            /* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */
            <span id={dialogLabelId} className="sr-only">
              Content Preview
            </span>
          )}
          renderBody={() => <EmbeddedContentPreview inputRef={inputRef} />}
        />
      )}
    </>
  )
}
